/**
 * Dashboard Layout
 * Main layout with sidebar navigation — legal-grade formal aesthetic
 */

import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/auth-context';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/theme-toggle';
import { cn } from '@/lib/utils';
import {
  Building2,
  Users,
  Briefcase,
  Ticket,
  LayoutDashboard,
  LogOut,
  ChevronLeft,
  FileText,
  AppWindow,
  ScrollText,
  Bell,
  Receipt,
  PiggyBank,
  BarChart3,
  Server,
  Globe,
  Calendar,
  Activity,
  FolderOpen,
  Settings2,
  CheckCircle2,
  FileBarChart,
  Clock,
  Mail,
  TrendingDown,
  TrendingUp,
  Calculator,
  Shield,
  Package,
  Gauge,
  MessageSquareText,
  BookOpen,
  Zap,
  Wrench,
  BellRing,
  MonitorCheck,
  CalendarCheck,
  Plug,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { DashboardLabels, type DashboardTypeValue } from '@bizsolutions/shared';

interface DashboardLayoutProps {
  dashboard: DashboardTypeValue;
}

type NavItem = { label: string; icon: React.ReactNode; path: string };
type NavSection = { section: string; items: NavItem[] };
type NavConfig = (NavItem | NavSection)[];

function isNavSection(item: NavItem | NavSection): item is NavSection {
  return 'section' in item;
}

const navigationItems: Record<DashboardTypeValue, NavConfig> = {
  customer_operations: [
    { label: 'Accounts', icon: <Building2 className="h-4 w-4" />, path: 'accounts' },
    { label: 'Contacts', icon: <Users className="h-4 w-4" />, path: 'contacts' },
    { label: 'Deals', icon: <Briefcase className="h-4 w-4" />, path: 'deals' },
    { label: 'Activities', icon: <Activity className="h-4 w-4" />, path: 'activities' },
    { label: 'Documents', icon: <FolderOpen className="h-4 w-4" />, path: 'documents' },
    { label: 'Calendar', icon: <Calendar className="h-4 w-4" />, path: 'calendar' },
    { label: 'Outlook Sync', icon: <Mail className="h-4 w-4" />, path: 'outlook-sync' },
  ],
  helpdesk_support: [
    { label: 'Tickets', icon: <Ticket className="h-4 w-4" />, path: 'tickets' },
    { label: 'Knowledge Base', icon: <BookOpen className="h-4 w-4" />, path: 'knowledge-base' },
    { label: 'Canned Responses', icon: <MessageSquareText className="h-4 w-4" />, path: 'canned-responses' },
    { label: 'SLA Policies', icon: <Gauge className="h-4 w-4" />, path: 'sla-policies' },
    { label: 'Automation Rules', icon: <Zap className="h-4 w-4" />, path: 'automation-rules' },
    { label: 'App Status', icon: <MonitorCheck className="h-4 w-4" />, path: 'app-status' },
    { label: 'Maintenance', icon: <Wrench className="h-4 w-4" />, path: 'maintenance' },
    { label: 'Analytics', icon: <BarChart3 className="h-4 w-4" />, path: 'analytics' },
  ],
  billing_finance: [
    { label: 'Overview', icon: <LayoutDashboard className="h-4 w-4" />, path: 'overview' },
    {
      section: 'Revenue',
      items: [
        { label: 'Revenue', icon: <TrendingUp className="h-4 w-4" />, path: 'revenue' },
        { label: 'Contracts', icon: <ScrollText className="h-4 w-4" />, path: 'contracts' },
      ],
    },
    {
      section: 'Expenses',
      items: [
        { label: 'Expenses', icon: <Receipt className="h-4 w-4" />, path: 'expenses' },
        { label: 'Bill Pay', icon: <CalendarCheck className="h-4 w-4" />, path: 'bill-pay' },
        { label: 'Assets', icon: <Package className="h-4 w-4" />, path: 'assets' },
      ],
    },
    {
      section: 'Planning',
      items: [
        { label: 'Budgets', icon: <PiggyBank className="h-4 w-4" />, path: 'budgets' },
        { label: 'Forecast', icon: <TrendingUp className="h-4 w-4" />, path: 'forecast' },
      ],
    },
    {
      section: 'Reports',
      items: [
        { label: 'Statements', icon: <FileBarChart className="h-4 w-4" />, path: 'statements' },
        { label: 'Email Logs', icon: <Mail className="h-4 w-4" />, path: 'email-logs' },
      ],
    },
    {
      section: 'Workspace',
      items: [
        { label: 'Documents', icon: <FolderOpen className="h-4 w-4" />, path: 'documents' },
        { label: 'Calendar', icon: <Calendar className="h-4 w-4" />, path: 'calendar' },
      ],
    },
    {
      section: 'Setup',
      items: [
        { label: 'Business Profiles', icon: <Building2 className="h-4 w-4" />, path: 'business-profiles' },
        { label: 'Applications', icon: <AppWindow className="h-4 w-4" />, path: 'applications' },
      ],
    },
  ],
  tax_compliance: [
    { label: 'Overview', icon: <LayoutDashboard className="h-4 w-4" />, path: 'overview' },
    { label: 'Deductions', icon: <TrendingDown className="h-4 w-4" />, path: 'deductions' },
    { label: 'Tax Forms', icon: <FileText className="h-4 w-4" />, path: 'forms' },
    { label: 'Estimated Taxes', icon: <Calculator className="h-4 w-4" />, path: 'estimated' },
    { label: 'Projections', icon: <TrendingUp className="h-4 w-4" />, path: 'projections' },
    { label: 'Audit Log', icon: <Shield className="h-4 w-4" />, path: 'audit-log' },
  ],
  project_management: [
    { label: 'Overview', icon: <LayoutDashboard className="h-4 w-4" />, path: 'overview' },
    { label: 'Projects', icon: <Briefcase className="h-4 w-4" />, path: 'list' },
    { label: 'Tasks', icon: <CheckCircle2 className="h-4 w-4" />, path: 'tasks' },
    { label: 'Time Tracking', icon: <Clock className="h-4 w-4" />, path: 'time-entries' },
    { label: 'Requirements', icon: <FileBarChart className="h-4 w-4" />, path: 'requirements' },
    { label: 'Risk Register', icon: <Shield className="h-4 w-4" />, path: 'risks' },
  ],
  // Service Delivery. The nav is filtered by role at render time: an intern
  // sees only 'My Work' and 'My Time'. The sidebar is a convenience — every
  // route behind it is independently gated server-side.
  service_delivery: [
    { label: 'Overview', icon: <LayoutDashboard className="h-4 w-4" />, path: 'overview' },
    { label: 'My Work', icon: <CheckCircle2 className="h-4 w-4" />, path: 'my-work' },
    { label: 'Engagements', icon: <Briefcase className="h-4 w-4" />, path: 'engagements' },
    { label: 'Service Plans', icon: <Package className="h-4 w-4" />, path: 'plans' },
    { label: 'Work & Deliverables', icon: <FolderOpen className="h-4 w-4" />, path: 'work' },
    { label: 'Review Queue', icon: <MonitorCheck className="h-4 w-4" />, path: 'review' },
    { label: 'Time Tracking', icon: <Clock className="h-4 w-4" />, path: 'time' },
    { label: 'Performance', icon: <TrendingUp className="h-4 w-4" />, path: 'performance' },
    { label: 'Profitability', icon: <Gauge className="h-4 w-4" />, path: 'profitability' },
  ],
  analytics: [
    { label: 'Overview', icon: <BarChart3 className="h-4 w-4" />, path: 'overview' },
    { label: 'Report Builder', icon: <FileBarChart className="h-4 w-4" />, path: 'report-builder' },
    { label: 'Scheduled Reports', icon: <Clock className="h-4 w-4" />, path: 'scheduled-reports' },
  ],
  system_admin: [
    { label: 'System Status', icon: <Server className="h-4 w-4" />, path: 'system-status' },
    { label: 'Rate Limit Activity', icon: <Shield className="h-4 w-4" />, path: 'rate-limit-activity' },
    { label: 'Widget Management', icon: <Globe className="h-4 w-4" />, path: 'widget-management' },
    { label: 'Email Templates', icon: <Mail className="h-4 w-4" />, path: 'email-templates' },
    { label: 'Integrations', icon: <Plug className="h-4 w-4" />, path: 'integrations' },
    { label: 'Lookup Admin', icon: <Settings2 className="h-4 w-4" />, path: 'lookup-admin' },
    { label: 'Approvals', icon: <CheckCircle2 className="h-4 w-4" />, path: 'approvals' },
    { label: 'Notifications', icon: <BellRing className="h-4 w-4" />, path: 'notification-prefs' },
  ],
};

/**
 * Nav paths a restricted role may see within a dashboard.
 *
 * Presentation only. Every route behind these is gated server-side, and hiding
 * a link has never stopped anyone typing a URL — this exists so the intern's
 * sidebar reflects what they can actually do, not as a control.
 */
const RESTRICTED_NAV_PATHS: Partial<Record<DashboardTypeValue, string[]>> = {
  service_delivery: ['my-work', 'time'],
};

/** Roles below `user` are restricted; see packages/shared roleHierarchy. */
function isRestrictedRole(role: string | undefined): boolean {
  return role === 'intern';
}

function filterNav(config: NavConfig, dashboard: DashboardTypeValue, role?: string): NavConfig {
  if (!isRestrictedRole(role)) return config;
  const allowed = RESTRICTED_NAV_PATHS[dashboard];
  if (!allowed) return [];
  const keep = (i: NavItem) => allowed.includes(i.path);
  return config
    .map((entry) =>
      isNavSection(entry) ? { ...entry, items: entry.items.filter(keep) } : entry
    )
    .filter((entry) => (isNavSection(entry) ? entry.items.length > 0 : keep(entry)));
}

export function DashboardLayout({ dashboard }: DashboardLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = filterNav(navigationItems[dashboard] || [], dashboard, user?.role);

  // Fetch alert counts for billing dashboard
  const { data: alertData } = useQuery({
    queryKey: ['alertCounts'],
    queryFn: () => api.getAlertCounts(),
    enabled: dashboard === 'billing_finance' && !!user,
    refetchInterval: 24 * 60 * 60 * 1000, // poll once a day
  });
  const alertCounts = alertData?.data;

  // Open-ticket count for the Help Desk nav badge (status 'open' = new/untouched).
  const { data: openTicketsData } = useQuery({
    queryKey: ['open-ticket-count'],
    queryFn: () => api.getTickets({ status: 'open', limit: 1 }),
    enabled: dashboard === 'helpdesk_support' && !!user,
    refetchInterval: 60_000, // refresh every minute
  });
  const openTicketCount = openTicketsData?.pagination?.total ?? 0;

  return (
    <div className="h-screen flex overflow-hidden bg-legal-warm/40">
      {/* Sidebar — dark, formal */}
      <aside className="w-64 shrink-0 bg-primary text-primary-foreground flex flex-col h-full">
        {/* Logo / Header */}
        <div className="shrink-0 px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <img src="/logo-sidebar.png" alt="BizSolutionsMate" className="h-7 w-auto" />
            <h1 className="text-lg font-serif font-semibold tracking-tight">BizSolutionsMate</h1>
          </div>
          <p className="text-xs text-primary-foreground/60 mt-1.5 ml-[38px]">{DashboardLabels[dashboard]}</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 px-3 py-3 overflow-y-auto">
          <ul className="space-y-0.5">
            {navItems.map((entry, idx) => {
              if (isNavSection(entry)) {
                return (
                  <li key={entry.section}>
                    <p className={cn(
                      'px-3 pt-3 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground/40',
                      idx === 0 && 'pt-0'
                    )}>
                      {entry.section}
                    </p>
                    <ul className="space-y-0.5">
                      {entry.items.map((item) => (
                        <li key={item.path}>
                          <NavLink
                            to={item.path}
                            className={({ isActive }) =>
                              cn(
                                'flex items-center gap-3 px-3 py-1.5 rounded text-[13px] font-medium transition-colors',
                                isActive
                                  ? 'bg-white/15 text-primary-foreground'
                                  : 'text-primary-foreground/65 hover:bg-white/8 hover:text-primary-foreground/90'
                              )
                            }
                          >
                            {item.icon}
                            {item.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              }
              return (
                <li key={entry.path}>
                  <NavLink
                    to={entry.path}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-1.5 rounded text-[13px] font-medium transition-colors',
                        isActive
                          ? 'bg-white/15 text-primary-foreground'
                          : 'text-primary-foreground/65 hover:bg-white/8 hover:text-primary-foreground/90'
                      )
                    }
                  >
                    {entry.icon}
                    <span>{entry.label}</span>
                    {dashboard === 'helpdesk_support' && entry.path === 'tickets' && openTicketCount > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold tabular-nums">
                        {openTicketCount}
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="shrink-0 px-3 py-3 border-t border-white/10">
          <Button
            variant="ghost"
            className="w-full justify-start text-primary-foreground/60 hover:text-primary-foreground hover:bg-white/8 text-[13px]"
            onClick={() => navigate('/dashboard')}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Dashboards
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header — clean, minimal */}
        <header className="h-14 shrink-0 bg-background border-b flex items-center justify-between px-6">
          <div className="flex items-center gap-2">
            <span className="font-serif font-medium text-foreground">{DashboardLabels[dashboard]}</span>
          </div>

          <div className="flex items-center gap-4">
            {dashboard === 'billing_finance' && (
              <Button
                variant="ghost"
                size="sm"
                className="relative"
                onClick={() => navigate('alerts')}
              >
                <Bell className="h-4 w-4" />
                {alertCounts && alertCounts.totalUnread > 0 && (
                  <span className={cn(
                    'absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1',
                    alertCounts.criticalCount > 0 ? 'bg-destructive' : 'bg-legal-gold'
                  )}>
                    {alertCounts.totalUnread}
                  </span>
                )}
              </Button>
            )}
            <ThemeToggle />
            <div className="h-5 w-px bg-border" />
            <span className="text-sm text-muted-foreground">
              {user?.firstName} {user?.lastName}
            </span>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
